<template>
  <v-row justify="center">
    <v-dialog :model-value="show" persistent max-width="720px">
      <v-card class="v-card-100">
        <!-- Header -->
        <v-card class="v-card-100 mt-0 custom-shadow">
          <v-card-title class="pa-0">
            <v-row class="ma-2">
              <span class="font-weight-bold">Test Report</span>
              <v-spacer></v-spacer>
              <v-icon @click="close">mdi-close</v-icon>
            </v-row>
          </v-card-title>
        </v-card>

        <!-- Body -->
        <v-card-text>
          <v-container class="pa-0">
            <!-- Tabs -->
            <v-tabs v-model="tab_index" class="custom-tabs">
              <v-tab
                v-for="(tab, index) in tabs"
                :key="index"
                class="component-tab-heading text-none"
                ripple
                @click="changeTab(tab)"
              >
                {{ tab }}
              </v-tab>
            </v-tabs>

            <!-- Basic Info -->
            <div class="tag-details-style">
              <v-row>
                <v-col cols="12" sm="3">
                  <p>Tag ID</p>
                  <p class="detail-heading">
                    {{ item.tag_id || item.tag_number || "-" }}
                  </p>
                </v-col>
                <v-col cols="12" sm="3">
                  <p>Article ID</p>
                  <p class="detail-heading">
                    {{ item.sku_code || "-" }}
                  </p>
                </v-col>
                <v-col cols="12" sm="3">
                  <p>Grading Date</p>
                  <p class="detail-heading">
                    {{ gradingDate }}
                  </p>
                </v-col>
                <v-col cols="12" sm="3">
                  <p>Graded By</p>
                  <p class="detail-heading">{{ gradedBy }}</p>
                </v-col>
              </v-row>

              <v-row style="margin-top: -18px;">
                <v-col cols="12" sm="3">
                  <p>Article Description</p>
                  <p class="detail-heading">{{ item.sku_description || "-" }}</p>
                </v-col>
              </v-row>
            </div>

            <!-- Grade Result -->
            <div style="padding:10px; text-align: center;">
              <div
                class="grade-point"
                :class="{
                  'accept-grade': finalGrade === 'Accept',
                  'reject-grade': finalGrade === 'Reject'
                }"
              >
                {{ finalGrade || '-' }}
              </div>
            </div>

            <!-- Grading Result Sections -->
            <section v-if="hasGradingResult">
              <div
                v-for="(tests, sectionName) in gradingResult"
                :key="sectionName"
                class="mb-4"
              >
                <!-- Section title -->
                <h5 class="font-weight-bold mb-2">
                  {{ formatSectionName(sectionName) }}
                </h5>

                <!-- Two-column test/answer layout -->
                <v-row>
                  <v-col
                    v-for="(testItem, idx) in tests"
                    :key="idx"
                    cols="12"
                    sm="6"
                  >
                    <div>{{ testItem.test }}</div>
                    <div class="font-weight-bold">{{ testItem.value[0] || '-' }}</div>
                  </v-col>
                </v-row>
              </div>

              <!-- Images -->
              <div>
                <h5 class="font-weight-bold mb-2">Images & Videos</h5>
                <v-slide-group v-model="currentIndex" class="py-0" show-arrows center-active>
                  <v-slide-group-item
                    v-for="(imageUrl, index) in mergedImages"
                    :key="index"
                    :value="index"
                    class="thumb-wrapper"
                  >
                    <div
                      class="thumb-container"
                      :class="{ 'thumb-active': index === currentIndex }"
                    >
                      <img :src="imageUrl" class="thumb-img" @click="openDialog(index)" />
                    </div>
                  </v-slide-group-item>
                </v-slide-group>
              </div>
            </section>

            <!-- No data -->
            <section v-else>
              <span>No data found</span>
            </section>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Full image viewer -->
    <v-dialog v-model="fullImageShow" persistent max-width="600">
      <v-card>
        <v-card-title class="pa-0">
          <v-row class="ma-2">
            <span class="font-weight-bold">View Image</span>
            <v-spacer></v-spacer>
            <v-icon @click="fullImageShow = false">mdi-close</v-icon>
          </v-row>
        </v-card-title>
        <v-card-text class="d-flex align-center justify-center">
          <v-btn icon @click="prevImage" :disabled="currentIndex === 0">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <v-img :src="mergedImages[currentIndex]" max-height="80vh" contain />

          <v-btn
            icon
            @click="nextImage"
            :disabled="currentIndex === mergedImages.length - 1"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
export default {
  name: "GradingDetailsDialog",
  props: {
    show: Boolean,
    item: { type: Object, required: true }
  },
  data() {
    return {
      tabs: ["Service Inspection", "Packaging & Validation"],
      active_tab: "Service Inspection",
      tab_index: 0,
      fullImageShow: false,
      currentIndex: 0
    };
  },
  computed: {
    gradingDate() {
      const details =
        this.active_tab === "Service Inspection"
          ? this.item?.customer_declaration_result?.grader_details
          : this.item?.grading_result?.grader_details;
      return details?.grading_date
        ? details.grading_date.split("-").reverse().join("-")
        : "-";
    },
    gradedBy() {
      const details =
        this.active_tab === "Service Inspection"
          ? this.item?.customer_declaration_result?.grader_details
          : this.item?.grading_result?.grader_details;
      return details?.graded_by_name || "-";
    },
    finalGrade() {
      return this.active_tab === "Service Inspection"
        ? this.item?.customer_declaration_result?.final_grade
        : this.item?.grading_result?.final_grade;
    },
    gradingResult() {
      return this.active_tab === "Service Inspection"
        ? this.item?.customer_declaration_result?.final_grading_result || {}
        : this.item?.grading_result?.final_grading_result || {};
    },
    hasGradingResult() {
      return (
        this.gradingResult && Object.keys(this.gradingResult).length > 0
      );
    },
    mergedImages() {
      let results = this.gradingResult;
      let urls = [];
      Object.values(results).forEach((testArray) => {
        testArray.forEach((test) => {
          if (Array.isArray(test.imageHolder)) {
            test.imageHolder.forEach((imgObj) => {
              if (imgObj.imageSrc) urls.push(imgObj.imageSrc);
              if (imgObj.originalImageSrc) urls.push(imgObj.originalImageSrc);
              if (imgObj.annotatedImageSrc) urls.push(imgObj.annotatedImageSrc);
            });
          }
        });
      });
      return [...new Set(urls)];
    }
  },
  methods: {
    changeTab(tab) {
      this.active_tab = tab;
    },
    openDialog(index) {
      this.currentIndex = index;
      this.fullImageShow = true;
    },
    prevImage() {
      if (this.currentIndex > 0) this.currentIndex--;
    },
    nextImage() {
      if (this.currentIndex < this.mergedImages.length - 1) this.currentIndex++;
    },
    close() {
      this.$emit("update:show", false);
    },
    formatSectionName(name) {
      return name.replace(" Condition", "");
    }
  }
};
</script>
 <style scoped>
  .fw-600 {
    font-weight: 600;
  }
  
  .fw-500 {
    font-weight: 500;
  }
  
  .fs-md {
    font-size: medium;
  }
  .customer-details {
    background-color: #EAF4FF;
  }
  .customer-details label {
    color: #002F9E;
    font-weight: 600;
    font-size: 18px;
  }
  .detail-heading {
    font-weight: 600;
    color: #2D2D2D;
    font-size: 14px;
  }
  .v-tooltip__content {
  background-color: white !important; 
  color: black !important;           
  border: 1px solid #ccc;            
  padding: 10px;                     
  font-size: 14px;                  
  }
  .disabled_span {
    pointer-events: none; 
    opacity: 0.5;
    color: gray;
  }
  .thumb-wrapper {
    flex: 0 0 auto !important;
    margin-right: 8px;
  }

  .thumb-container {
    width: 90px;
    height: 95px;
    border-radius: 12px !important;   /* 🔒 Ensures radius on container */
    overflow: hidden;
    cursor: pointer;
    border: 2px solid transparent;
    box-sizing: border-box;
  }

  .thumb-img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    border-radius: 12px !important;   /* 🔒 Force radius on image too */
  }
  ::v-deep(.v-slide-group__container .v-slide-group__content) {
    display: flex !important;
    justify-content: left !important;
    align-items: left !important;
  }
  .v-btn__content{
    font-size: 16px !important;
  }
  .media-content {
    max-width: 300px;
    max-height: 400px;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
  }
  :deep(.custom-tabs .v-tab--selected) {
      text-transform: none;
      color: #002F9E;
      height: 36px !important;
      background-color:#E1F1FF !important;
      font-size: 12px;
      font-weight: 500 !important;
      line-height: 16.8px !important;
  }
  .tag-details-style{
    background-color: #EAF4FF;
    border-radius: 8px;
    padding: 10px;
  }
  .grade-point{
    width: 80px;
    padding: 7px;
    position: relative;
    left: 41%;
    font-weight: 600;
  }
  .dynamic-height-dis-card {
    height: calc(var(--vh, 1vh) * 100 - 500px) !important;
  }
  .accept-grade {
    background: #E1FAE1;
    color: #157F15;
  }

  /* Reject */
  .reject-grade {
    background: #FDEAEA;
    color: #C62828;
  }

  </style>
